import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { MenuType } from "@/types/common";
import Link from "next/link";
import { usePathname } from "next/navigation";

export function DropdownMenu({ data }: { data: MenuType[] }) {
  const pathName = usePathname();

  // Function to find the best matching group for current path
  const getActiveGroupIndex = () => {
    let bestMatch = -1;
    let longestMatch = 0;

    data.forEach((group, index) => {
      // Check direct pathGroup match
      if (group.pathGroup && pathName.includes(group.pathGroup)) {
        if (group.pathGroup.length > longestMatch) {
          longestMatch = group.pathGroup.length;
          bestMatch = index;
        }
      }

      // Check sub-items match
      if (group.items) {
        group.items.forEach((item) => {
          item.items?.forEach((subItem) => {
            if (
              subItem.url &&
              pathName.includes(subItem.url) &&
              subItem.url.length > longestMatch
            ) {
              longestMatch = subItem.url.length;
              bestMatch = index;
            }
          });
        });
      }
    });

    return bestMatch;
  };

  const activeGroupIndex = getActiveGroupIndex();
  return (
    <Menubar
      key={pathName}
      data-testid="dropdown-menu"
      data-menu="true"
      className="border-0 bg-transparent shadow-none h-full p-0 gap-x-7.5"
    >
      {data.map((group, i) => (
        <MenubarMenu key={group.title || "" + i}>
          {group.items?.length ?? 0 > 0 ? (
            <>
              <MenubarTrigger
                data-testid={`dropdown-menu-trigger-${group.id}`}
                data-active={activeGroupIndex === i}
                className="data-[state=open]:border-b-primary data-[active=true]:border-b-primary rounded-none px-0 focus:bg-background data-[state=open]:bg-background data-[state=open]:text-foreground text-base text-z border-t-4 border-b-4 border-transparent mb-[-2px] text-nowrap h-full flex items-center font-semibold"
              >
                {group.title}
              </MenubarTrigger>
              <MenubarContent
                data-testid={`dropdown-menu-content-${group.id}`}
                data-menu="open"
                className="grid z-50 border-0 bg-transparent grid-cols-6 gap-4 shadow-none px-0 -mt-2"
              >
                {group.items?.map((item, j) => (
                  <MenubarItem
                    key={j}
                    data-testid={`dropdown-menu-item-${item.id}`}
                    className="text-xs focus:text-gray-700 text-gray-700 gap-y-2 gap-x-4 items-start justify-start focus:bg-transparent flex flex-col hover:bg-transparent cursor-pointer"
                  >
                    <>
                      <div className="text-gray-900 text-base font-semibold">
                        {item.groupName}
                      </div>
                      {item.items?.map((subItem, sIdx) => (
                        <Link
                          key={sIdx}
                          data-testid={`dropdown-menu-link-${subItem.id}`}
                          href={subItem.url ?? ""}
                          className={subItem.className}
                          onClick={() => {
                            // blur active element so Menubar/Popover loses focus and closes
                            try {
                              (document.activeElement as HTMLElement)?.blur();
                            } catch {
                              /* ignore */
                            }
                          }}
                        >
                          {subItem.title}
                        </Link>
                      ))}{" "}
                    </>
                  </MenubarItem>
                ))}
              </MenubarContent>
            </>
          ) : (
            <Link
              data-testid={`dropdown-menu-link-${group.id}`}
              data-active={activeGroupIndex === i}
              className="data-[active=true]:border-b-primary border-t-4 border-b-4 border-transparent mb-[-2px] text-nowrap h-full flex items-center font-semibold"
              href={group.url ?? ""}
              onClick={() => {
                try {
                  (document.activeElement as HTMLElement)?.blur();
                } catch {
                  /* ignore */
                }
              }}
            >
              {group.title}
            </Link>
          )}
        </MenubarMenu>
      ))}
    </Menubar>
  );
}
